import { useCallback, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { useWeekStart, weekdayInitials } from '../weekStartPreference';
import { isSameDay, startOfDay, toDateKey, weekDays, type EventOccurrence } from '@awwyeah/shared';
import { dayDots, isUnassignedDot } from '../dayMarks';
import { styleForOwners, UNASSIGNED_DOT_CLASS, type MemberView } from '../members';
import { blockLabel, shortTime, WEEK_GRID_PX } from '../weekBlocks';
import {
  anchorChange,
  CENTER_PANE,
  entryPaneFor,
  offsetForPane,
  paneIndexFor,
  shiftForPane,
  weekPanes,
} from '../weekCarousel';

/** The grid covers 6a–10p; anything outside is clamped to the edges. */
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;
const HOUR_LABELS = [6, 9, 12, 15, 18, 21];
/** How long after the last scroll event a snap counts as settled. */
const SETTLE_MS = 110;
/** Long enough to cover a `behavior: 'smooth'` scroll before we listen again. */
const ANIMATION_MS = 420;

/**
 * Overlapping events share the column: each gets a lane, so a 5–9pm shift and a 7–9pm
 * date night are both visible instead of one hiding the other.
 */
function laneOut(
  occurrences: readonly EventOccurrence[],
): { occurrence: EventOccurrence; lane: number; lanes: number }[] {
  const sorted = [...occurrences].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const out: { occurrence: EventOccurrence; lane: number; lanes: number }[] = [];
  // Lane counting resets per cluster of overlapping events, so one busy evening doesn't
  // shrink the morning's bars.
  let cluster: { occurrence: EventOccurrence; lane: number }[] = [];
  let laneEnds: number[] = [];

  const flush = (): void => {
    const lanes = Math.max(1, laneEnds.length);
    for (const entry of cluster) out.push({ ...entry, lanes });
    cluster = [];
    laneEnds = [];
  };

  for (const occurrence of sorted) {
    const startMs = Date.parse(occurrence.start);
    if (laneEnds.length > 0 && laneEnds.every((end) => end <= startMs)) flush();
    let lane = laneEnds.findIndex((end) => end <= startMs);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = Date.parse(occurrence.end);
    cluster.push({ occurrence, lane });
  }
  flush();
  return out;
}

/** Multi-line ellipsis for a tall block's title — CSS line-clamp, inline because the line
 * count comes from the block's height. */
function clampLines(lines: number): CSSProperties {
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
  };
}

function positionOf(startMs: number, endMs: number, day: Date): { top: number; height: number } {
  const dayStart = startOfDay(day).getTime();
  const windowStart = dayStart + DAY_START_HOUR * 3_600_000;
  const windowEnd = dayStart + DAY_END_HOUR * 3_600_000;
  const span = windowEnd - windowStart;
  const top = ((Math.max(startMs, windowStart) - windowStart) / span) * 100;
  const height = ((Math.min(endMs, windowEnd) - Math.max(startMs, windowStart)) / span) * 100;
  return { top, height: Math.max(height, 2) };
}

export interface WeekViewProps {
  anchor: Date;
  today: Date;
  now: Date;
  byDay: ReadonlyMap<string, EventOccurrence[]>;
  members: ReadonlyMap<string, MemberView>;
  onSelectDay: (day: Date) => void;
  onOpenOccurrence: (occurrence: EventOccurrence) => void;
  /** Move the anchor a whole week — raised when a swipe settles on a neighbouring pane. */
  onNavigateWeek: (direction: 1 | -1) => void;
}

/**
 * Week view (design Screen C · 6) as a three-pane scroll-snap carousel.
 *
 * Usability testing 2026-08-01 (Faye, iPhone): "I want to be able to smoothly scroll the
 * weekly view" — paging used to jump a whole screen with no motion. Previous / current /
 * next weeks are all rendered inside one horizontally scrolling, snapping track: the
 * browser provides momentum and axis-locking (so vertical page scrolling still works), and
 * when a snap settles the anchor advances a week and the track recentres invisibly. The
 * header buttons come through `anchor` and get an eased slide instead.
 */
export function WeekView({
  anchor,
  today,
  now,
  byDay,
  members,
  onSelectDay,
  onOpenOccurrence,
  onNavigateWeek,
}: WeekViewProps): JSX.Element {
  const { weekStartsOn } = useWeekStart();
  const panes = weekPanes(anchor, weekStartsOn);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** True while we are positioning the track ourselves — scroll events must be ignored. */
  const positioning = useRef(true);
  /** True when this anchor change came from a swipe, so it must not animate again. */
  const fromSwipe = useRef(false);
  const previousAnchor = useRef(anchor);

  const settle = useCallback(() => {
    const element = scrollerRef.current;
    if (!element) return;
    const shift = shiftForPane(paneIndexFor(element.scrollLeft, element.clientWidth));
    if (shift === 0) return;
    fromSwipe.current = true;
    positioning.current = true;
    onNavigateWeek(shift);
  }, [onNavigateWeek]);

  const onScroll = useCallback((): void => {
    if (positioning.current) return;
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(settle, SETTLE_MS);
  }, [settle]);

  // Position the track for the anchor we're now showing: instantly when the swipe already
  // moved the viewport (or on a jump like "Today"), eased when a button moved the anchor.
  useLayoutEffect(() => {
    const element = scrollerRef.current;
    if (!element) return undefined;
    const width = element.clientWidth;
    const change = fromSwipe.current
      ? 'none'
      : anchorChange(previousAnchor.current, anchor, weekStartsOn);
    previousAnchor.current = anchor;
    fromSwipe.current = false;

    positioning.current = true;
    element.scrollLeft = offsetForPane(entryPaneFor(change), width);
    const animated = change === 'next' || change === 'prev';
    if (animated) {
      element.scrollTo({ left: offsetForPane(CENTER_PANE, width), behavior: 'smooth' });
    }
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(
      () => {
        positioning.current = false;
      },
      animated ? ANIMATION_MS : 0,
    );
    return undefined;
  }, [anchor, weekStartsOn]);

  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
    },
    [],
  );

  return (
    <div
      ref={scrollerRef}
      onScroll={onScroll}
      data-testid="week-carousel"
      className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {panes.map((paneAnchor, index) => (
        <div
          key={toDateKey(paneAnchor)}
          className="w-full shrink-0 snap-center"
          aria-hidden={index === CENTER_PANE ? undefined : true}
          data-testid={index === CENTER_PANE ? 'week-pane-current' : 'week-pane'}
        >
          <WeekPane
            anchor={paneAnchor}
            today={today}
            now={now}
            byDay={byDay}
            members={members}
            interactive={index === CENTER_PANE}
            onSelectDay={onSelectDay}
            onOpenOccurrence={onOpenOccurrence}
          />
        </div>
      ))}
    </div>
  );
}

function WeekPane({
  anchor,
  today,
  now,
  byDay,
  members,
  interactive,
  onSelectDay,
  onOpenOccurrence,
}: {
  anchor: Date;
  today: Date;
  now: Date;
  byDay: ReadonlyMap<string, EventOccurrence[]>;
  members: ReadonlyMap<string, MemberView>;
  interactive: boolean;
  onSelectDay: (day: Date) => void;
  onOpenOccurrence: (occurrence: EventOccurrence) => void;
}): JSX.Element {
  const { weekStartsOn } = useWeekStart();
  const days = weekDays(anchor, weekStartsOn);
  const WEEKDAY_INITIALS = weekdayInitials(weekStartsOn);
  const nowPosition = positionOf(now.getTime(), now.getTime(), now).top;

  return (
    <div data-testid={interactive ? 'calendar-grid' : undefined} aria-label="Week">
      <div className="grid grid-cols-[28px_repeat(7,1fr)] border-b border-line">
        <span />
        {days.map((day, index) => {
          const dayOccurrences = (byDay.get(toDateKey(day)) ?? []).filter(
            (occurrence) => !occurrence.event.allDay,
          );
          const { dots, hidden } = dayDots(dayOccurrences, 4);
          return (
            <button
              key={toDateKey(day)}
              type="button"
              tabIndex={interactive ? undefined : -1}
              onClick={() => onSelectDay(day)}
              data-testid={interactive ? 'calendar-day-cell' : undefined}
              data-date={toDateKey(day)}
              className="flex min-h-[44px] flex-col items-center justify-center gap-0.5 py-1"
            >
              <span className="text-[10px] text-fg-3">{WEEKDAY_INITIALS[index]}</span>
              <span
                className={`text-sm font-semibold ${
                  isSameDay(day, today)
                    ? 'flex h-6 w-6 items-center justify-center rounded-full bg-inverse text-inverse-fg'
                    : ''
                }`}
              >
                {day.getDate()}
              </span>
              {/* Per-member dots, so a busy day is scannable without opening it (both
                  testers asked for this, 2026-08-01). */}
              <span className="mt-px flex h-[7px] items-center justify-center gap-[3px]">
                {dots.map((ownerId) => (
                  <span
                    key={ownerId}
                    data-testid="day-dot"
                    data-unassigned={isUnassignedDot(ownerId) || undefined}
                    title={
                      isUnassignedDot(ownerId)
                        ? 'Nobody in particular'
                        : (members.get(ownerId)?.name ?? 'Member')
                    }
                    style={
                      isUnassignedDot(ownerId) ? undefined : styleForOwners([ownerId], members).dot
                    }
                    className={`h-[6px] w-[6px] rounded-full ${
                      isUnassignedDot(ownerId) ? UNASSIGNED_DOT_CLASS : ''
                    }`}
                  />
                ))}
                {hidden > 0 && (
                  <span className="font-mono text-[8px] font-bold leading-none text-fg-3">
                    +{hidden}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-[28px_repeat(7,1fr)] border-b border-line bg-bg">
        <span className="pr-1 text-right font-mono text-[9px] leading-6 text-fg-3">all</span>
        {days.map((day) => {
          const allDay = (byDay.get(toDateKey(day)) ?? []).filter(
            (occurrence) => occurrence.event.allDay,
          );
          return (
            <span key={`allday-${toDateKey(day)}`} className="flex flex-col gap-px px-px py-0.5">
              {allDay.slice(0, 2).map((occurrence) => (
                <button
                  key={occurrence.key}
                  type="button"
                  tabIndex={interactive ? undefined : -1}
                  data-testid={interactive ? 'event-item' : undefined}
                  onClick={() => onOpenOccurrence(occurrence)}
                  title={occurrence.event.title}
                  style={{
                    ...styleForOwners(occurrence.event.ownerIds, members).edge,
                    ...styleForOwners(occurrence.event.ownerIds, members).tint,
                  }}
                  className="h-4 overflow-hidden rounded-sm border-l-[3px] px-0.5 text-left text-[9px] font-semibold"
                >
                  {occurrence.event.title}
                </button>
              ))}
            </span>
          );
        })}
      </div>

      {/* 420px is WEEK_GRID_PX — block labels size themselves against it. */}
      <div className="grid h-[420px] grid-cols-[28px_repeat(7,1fr)] bg-bg">
        <div className="flex flex-col justify-between py-1 pr-1 text-right">
          {HOUR_LABELS.map((hour) => (
            <span key={hour} className="font-mono text-[9px] text-fg-3">
              {hour % 12 === 0 ? 12 : hour % 12}
              {hour < 12 ? 'a' : 'p'}
            </span>
          ))}
        </div>
        {days.map((day) => {
          const key = toDateKey(day);
          const timed = (byDay.get(key) ?? []).filter((occurrence) => !occurrence.event.allDay);
          const isToday = isSameDay(day, today);
          return (
            <div
              key={`col-${key}`}
              className={`relative border-l border-line ${isToday ? 'bg-raised' : ''}`}
            >
              {laneOut(timed).map(({ occurrence, lane, lanes }) => {
                const { top, height } = positionOf(
                  Date.parse(occurrence.start),
                  Date.parse(occurrence.end),
                  day,
                );
                const width = 100 / lanes;
                const { event } = occurrence;
                const unassigned = event.ownerIds.length === 0;
                // Solid owner fill with the white/ink label colour that reads on it, in
                // either theme (the hex doesn't change with the theme, so neither does this).
                const fill = styleForOwners(event.ownerIds, members).fill;
                const label = blockLabel((height / 100) * WEEK_GRID_PX);
                return (
                  <button
                    key={occurrence.key}
                    type="button"
                    tabIndex={interactive ? undefined : -1}
                    data-testid={interactive ? 'event-item' : undefined}
                    title={event.title}
                    onClick={() => onOpenOccurrence(occurrence)}
                    style={{
                      top: `${top}%`,
                      height: `${height}%`,
                      left: `calc(${lane * width}% + 1px)`,
                      width: `calc(${width}% - 2px)`,
                      ...fill,
                    }}
                    className={`absolute flex flex-col items-stretch justify-start overflow-hidden rounded-sm text-left ${
                      label.tiny ? 'px-[2px]' : 'px-[3px] py-[2px]'
                    } ${unassigned ? UNASSIGNED_DOT_CLASS : ''}`}
                  >
                    <span
                      className={`block font-semibold ${
                        label.tiny ? 'text-[9px] leading-[10px]' : 'text-[10px] leading-[12px]'
                      } ${label.titleLines === 1 ? 'truncate' : ''}`}
                      style={label.titleLines > 1 ? clampLines(label.titleLines) : undefined}
                    >
                      {event.title}
                    </span>
                    {label.showTime && (
                      <span className="block truncate font-mono text-[9px] leading-[11px] opacity-80">
                        {shortTime(occurrence.start)}
                      </span>
                    )}
                  </button>
                );
              })}
              {isToday && nowPosition >= 0 && nowPosition <= 100 && (
                <span
                  className="pointer-events-none absolute inset-x-0 h-0.5 bg-accent"
                  style={{ top: `${nowPosition}%` }}
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
